//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { IAliasedDateRange } from '@components/date-range-picker/dateRangePickerModels';
import DateString from '@models/DateString';

/**
 * Pinterest Reporting Service
 *
 * Centralizes all Pinterest-specific business logic for reporting features.
 *
 * BUSINESS CONTEXT:
 * Pinterest has stricter API limitations compared to other social platforms:
 * - Audience data (followers, net change in followers): Only available for last 30 days
 * - Engagement data (impressions, engagements): Only available for last 90 days
 */

const PINTEREST_AUDIENCE_MAX_DAYS = 30;
const PINTEREST_ENGAGEMENT_MAX_DAYS = 90;

export interface PinterestRestrictions {
  canFetchAudienceData: boolean;
  canFetchEngagementData: boolean;
}

const getPinterestCutoffDate = (
  dateRange: IAliasedDateRange,
  maxDays: number,
): DateString => {
  const today = DateString.getToday(dateRange.timeZone);
  // Using -(maxDays - 1) to match date picker logic (today + N previous days = N+1 total days)
  return DateString.addDays(today, -(maxDays - 1));
};

const isDateRangeBeyondPinterestLimit = (
  dateRange: IAliasedDateRange,
  maxDays: number,
): boolean => {
  const cutoffDate = getPinterestCutoffDate(dateRange, maxDays);
  return DateString.compare(dateRange.startDate, cutoffDate) < 0;
};

export const canFetchPinterestAudienceData = (
  dateRange: IAliasedDateRange,
  isPinterestConnected: boolean,
): boolean => {
  if (!isPinterestConnected) {
    return false;
  }
  return !isDateRangeBeyondPinterestLimit(
    dateRange,
    PINTEREST_AUDIENCE_MAX_DAYS,
  );
};

export const canFetchPinterestEngagementData = (
  dateRange: IAliasedDateRange,
  isPinterestConnected: boolean,
): boolean => {
  if (!isPinterestConnected) {
    return false;
  }
  return !isDateRangeBeyondPinterestLimit(
    dateRange,
    PINTEREST_ENGAGEMENT_MAX_DAYS,
  );
};
